<template>
  <div class="security-settings">
    <h2>Security</h2>
    <p class="section-description">Manage your password and account security settings.</p>

    <div class="settings-card methods-card">
      <div class="card-header">
        <i class="fas fa-key"></i>
        <div>
          <h3>Sign-in methods</h3>
          <p>Use a password, Google, or Microsoft. Keep at least one method.</p>
        </div>
      </div>

      <ul class="method-list">
        <li class="method-row">
          <div>
            <strong>Password</strong>
            <p>{{ hasPassword ? 'You can sign in with email and password.' : 'No password set.' }}</p>
          </div>
          <button
            v-if="hasPassword && identities.length"
            type="button"
            class="btn btn-secondary"
            :disabled="methodLoading"
            @click="removePassword"
          >
            Remove password
          </button>
        </li>
        <li v-for="identity in identities" :key="identity.id" class="method-row">
          <div>
            <strong>{{ providerLabel(identity.provider) }}</strong>
            <p>{{ identity.email || userEmail }}</p>
          </div>
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="methodLoading || !canUnlink"
            @click="unlinkIdentity(identity)"
          >
            Unlink
          </button>
        </li>
      </ul>

      <div v-if="unlinkedProviders.length" class="link-providers">
        <p class="link-label">Add another method</p>
        <OAuthButtons
          mode="link"
          :show-linked-in="false"
          :providers="unlinkedProviders"
          @success="onLinked"
          @error="onLinkError"
        />
      </div>
      <div v-if="methodError" class="error-message">
        <i class="fas fa-exclamation-circle"></i> {{ methodError }}
      </div>
      <div v-if="methodSuccess" class="success-message">
        <i class="fas fa-check-circle"></i> {{ methodSuccess }}
      </div>
    </div>

    <!-- Change Password Section -->
    <div class="settings-card">
      <div class="card-header">
        <i class="fas fa-lock"></i>
        <div>
          <h3>Change Password</h3>
          <p>Update your password to keep your account secure.</p>
        </div>
      </div>

      <div v-if="isOAuthUser" class="oauth-notice">
        <i class="fas fa-info-circle"></i>
        <div>
          <strong>OAuth Account</strong>
          <p>
            Your account uses {{ oauthProvider }} sign-in. You don't have a password set.
            Click below to receive an email with a link to set one.
          </p>
          <button
            class="btn btn-secondary"
            @click="sendResetEmail"
            :disabled="resetEmailSent || resetEmailLoading"
          >
            <span v-if="resetEmailLoading" class="spinner"></span>
            <span v-else-if="resetEmailSent"><i class="fas fa-check"></i> Email Sent</span>
            <span v-else><i class="fas fa-envelope"></i> Send Password Setup Email</span>
          </button>
          <p v-if="resetEmailSent" class="success-note">
            Check your inbox at <strong>{{ userEmail }}</strong> for a link to set your password.
          </p>
        </div>
      </div>

      <form v-else @submit.prevent="handleChangePassword" class="password-form">
        <div class="form-group">
          <label for="currentPassword">Current Password</label>
          <div class="input-wrapper">
            <input
              :type="showCurrent ? 'text' : 'password'"
              id="currentPassword"
              v-model="currentPassword"
              class="form-control"
              required
              autocomplete="current-password"
              placeholder="Enter your current password"
            />
            <button type="button" class="toggle-btn" @click="showCurrent = !showCurrent">
              <i :class="showCurrent ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <div class="field-hint">
            <router-link to="/forgot-password">Forgot your current password?</router-link>
          </div>
        </div>

        <div class="form-group">
          <label for="newPassword">New Password</label>
          <div class="input-wrapper">
            <input
              :type="showNew ? 'text' : 'password'"
              id="newPassword"
              v-model="newPassword"
              class="form-control"
              required
              minlength="8"
              autocomplete="new-password"
              placeholder="Minimum 8 characters"
            />
            <button type="button" class="toggle-btn" @click="showNew = !showNew">
              <i :class="showNew ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm New Password</label>
          <div class="input-wrapper">
            <input
              :type="showConfirm ? 'text' : 'password'"
              id="confirmPassword"
              v-model="confirmPassword"
              class="form-control"
              required
              autocomplete="new-password"
              placeholder="Re-enter your new password"
            />
            <button type="button" class="toggle-btn" @click="showConfirm = !showConfirm">
              <i :class="showConfirm ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
        </div>

        <!-- Password Requirements -->
        <div class="requirements">
          <div :class="['requirement', { met: newPassword.length >= 8 }]">
            <i :class="newPassword.length >= 8 ? 'fas fa-check-circle' : 'fas fa-circle'"></i>
            At least 8 characters
          </div>
          <div :class="['requirement', { met: passwordsMatch && newPassword.length > 0 }]">
            <i :class="passwordsMatch && newPassword.length > 0 ? 'fas fa-check-circle' : 'fas fa-circle'"></i>
            Passwords match
          </div>
        </div>

        <div v-if="error" class="error-message">
          <i class="fas fa-exclamation-circle"></i> {{ error }}
        </div>
        <div v-if="successMessage" class="success-message">
          <i class="fas fa-check-circle"></i> {{ successMessage }}
        </div>

        <button
          type="submit"
          class="btn btn-primary"
          :disabled="loading || !canSubmit"
        >
          <span v-if="loading" class="spinner"></span>
          {{ loading ? 'Updating...' : 'Update Password' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';
import authService from '@/services/auth';
import OAuthButtons from '@/components/OAuthButtons.vue';

export default {
  name: 'SettingsSecurity',
  components: { OAuthButtons },

  data() {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      showCurrent: false,
      showNew: false,
      showConfirm: false,
      loading: false,
      error: '',
      successMessage: '',
      resetEmailLoading: false,
      resetEmailSent: false,
      methodLoading: false,
      methodError: '',
      methodSuccess: '',
    };
  },

  computed: {
    passwordsMatch() {
      return this.newPassword === this.confirmPassword;
    },

    canSubmit() {
      return (
        this.currentPassword.length > 0 &&
        this.newPassword.length >= 8 &&
        this.passwordsMatch
      );
    },

    isOAuthUser() {
      return !this.hasPassword;
    },

    hasPassword() {
      const user = authService.getUser();
      if (typeof user?.has_password === 'boolean') return user.has_password;
      return !user?.is_oauth_user;
    },

    identities() {
      const user = authService.getUser();
      return user?.auth_identities || [];
    },

    canUnlink() {
      return this.hasPassword || this.identities.length > 1;
    },

    unlinkedProviders() {
      const linked = new Set(this.identities.map((item) => item.provider));
      return ['google', 'microsoft'].filter((provider) => !linked.has(provider));
    },

    oauthProvider() {
      const user = authService.getUser();
      return user?.oauth_provider || this.identities[0]?.provider || 'social';
    },

    userEmail() {
      const user = authService.getUser();
      return user?.email || '';
    },
  },

  methods: {
    async handleChangePassword() {
      if (!this.canSubmit) return;

      this.loading = true;
      this.error = '';
      this.successMessage = '';

      try {
        await api.post('/users/password-change/', {
          current_password: this.currentPassword,
          new_password: this.newPassword,
        });

        this.successMessage = 'Your password has been updated successfully.';
        this.currentPassword = '';
        this.newPassword = '';
        this.confirmPassword = '';
      } catch (err) {
        this.error = err.response?.data?.error || 'Failed to update password. Please try again.';
      } finally {
        this.loading = false;
      }
    },

    providerLabel(provider) {
      const labels = { google: 'Google', microsoft: 'Microsoft', linkedin: 'LinkedIn', apple: 'Apple' };
      return labels[provider] || provider;
    },

    async onLinked() {
      this.methodError = '';
      this.methodSuccess = 'Sign-in method linked. You can use it the next time you log in.';
    },

    onLinkError(message) {
      this.methodSuccess = '';
      this.methodError = message || 'Could not link that sign-in method.';
    },

    async unlinkIdentity(identity) {
      this.methodLoading = true;
      this.methodError = '';
      this.methodSuccess = '';
      try {
        await authService.unlinkProvider(identity.id);
        this.methodSuccess = `${this.providerLabel(identity.provider)} unlinked.`;
      } catch (err) {
        this.methodError = err.response?.data?.error || 'Could not unlink that method.';
      } finally {
        this.methodLoading = false;
      }
    },

    async removePassword() {
      this.methodLoading = true;
      this.methodError = '';
      this.methodSuccess = '';
      try {
        await authService.removePasswordLogin();
        this.methodSuccess = 'Password sign-in removed. Use Google or Microsoft next time.';
      } catch (err) {
        this.methodError = err.response?.data?.error || 'Could not remove password sign-in.';
      } finally {
        this.methodLoading = false;
      }
    },

    async sendResetEmail() {
      this.resetEmailLoading = true;

      try {
        await api.post('/users/password-reset/send-self/');
        this.resetEmailSent = true;
      } catch (err) {
        // Still show success to avoid info leakage
        this.resetEmailSent = true;
      } finally {
        this.resetEmailLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.security-settings h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
}

.section-description {
  color: #6b7280;
  margin-bottom: 2rem;
}

.settings-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  max-width: 520px;
}

.methods-card {
  margin-bottom: 1.5rem;
}

.method-list {
  list-style: none;
  margin: 0 0 1.25rem;
  padding: 0;
}

.method-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.method-row:last-child {
  border-bottom: none;
}

.method-row p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.85rem;
}

.link-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f3f4f6;
}

.card-header i {
  font-size: 1.4rem;
  color: var(--color-primary, #1E40AF);
  margin-top: 0.2rem;
  flex-shrink: 0;
}

.card-header h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 0.25rem;
}

.card-header p {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0;
}

.oauth-notice {
  display: flex;
  gap: 1rem;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  padding: 1.25rem;
  align-items: flex-start;
}

.oauth-notice > i {
  color: #3b82f6;
  font-size: 1.1rem;
  margin-top: 0.15rem;
  flex-shrink: 0;
}

.oauth-notice strong {
  display: block;
  color: #1e40af;
  margin-bottom: 0.4rem;
}

.oauth-notice p {
  color: #374151;
  font-size: 0.9rem;
  margin: 0 0 1rem;
  line-height: 1.5;
}

.success-note {
  margin-top: 0.75rem !important;
  color: #059669 !important;
  font-size: 0.875rem !important;
}

.password-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-group label {
  font-weight: 600;
  font-size: 0.9rem;
  color: #374151;
}

.input-wrapper {
  position: relative;
}

.form-control {
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1rem;
  border: 1px solid #e1e5e9;
  border-radius: 8px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: var(--color-primary, #1E40AF);
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}

.toggle-btn {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
}

.toggle-btn:hover {
  color: #6b7280;
}

.field-hint {
  font-size: 0.8rem;
}

.field-hint a {
  color: #6b7280;
  text-decoration: none;
}

.field-hint a:hover {
  color: var(--color-primary, #1E40AF);
  text-decoration: underline;
}

.requirements {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: #f9fafb;
  padding: 0.875rem 1rem;
  border-radius: 8px;
  margin-top: -0.5rem;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #9ca3af;
}

.requirement.met {
  color: #10b981;
}

.requirement i {
  width: 1rem;
  text-align: center;
}

.error-message {
  color: #dc2626;
  background: #fef2f2;
  border: 1px solid #fecaca;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.success-message {
  color: #059669;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn {
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: var(--color-primary, #1E40AF);
  color: white;
  align-self: flex-start;
}

.btn-primary:hover:not(:disabled) {
  filter: brightness(110%);
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-secondary {
  background: #f3f4f6;
  color: #374151;
  margin-top: 0.25rem;
}

.btn-secondary:hover:not(:disabled) {
  background: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner {
  display: inline-block;
  width: 0.9rem;
  height: 0.9rem;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
